import { z } from "zod";

import { bookedAppointmentsReadMetadataSchema } from "@/shared/agent-api/booked-appointments-read-metadata";
import { versionedAppointmentSlotActionIdSchema } from "@/shared/common-schemas";
import { AppointmentType, SchedulingAction } from "@/shared/models/appointment";
import { CareMode } from "@/shared/models/care-mode";
import { voiceProviderIdentitySchema } from "@/shared/models/provider-name";
import { SchedulingPriority } from "@/shared/models/referral";
import { PatientSchedulingInsuranceStatus, SelfPayFollowupStatus } from "@/shared/models/scheduling-self-pay";
import { schedulingPresentationSchema, schedulingServiceSchema } from "@/shared/scheduling/presentation";
import { availabilitySearchCoverageSchema, type DateRange } from "@/shared/scheduling/search-coverage";
import { northAmericanPhoneNumberSchema } from "@/shared/utils/phone-utils";

export interface Address {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
}

export const agentPatientSchema = z.object({
  id: z.string().min(1),
  givenName: z.string().min(1),
  familyName: z.string().min(1),
  dateOfBirth: z.string().date(),
});

export type Patient = z.infer<typeof agentPatientSchema>;

export enum PatientMatchMethod {
  EHR_ONLY = "EHR_ONLY",
  FUZZY_NAME_AND_DOB = "FUZZY_NAME_AND_DOB",
  NAME_AND_DOB = "NAME_AND_DOB",
  PHONE_AND_DOB = "PHONE_AND_DOB",
  PHONE_AND_NAME = "PHONE_AND_NAME",
  PHONE_NAME_AND_DOB = "PHONE_NAME_AND_DOB",
}

/** Whether the authoritative EHR chart has at least one usable NANP callback number. */
export enum PatientContactPhoneStatus {
  Present = "present",
  Missing = "missing",
  Unknown = "unknown",
}

export enum PatientIntakeStatus {
  CarrierNotAccepted = "carrier_not_accepted",
  Created = "created",
  Matched = "matched",
}

export const createPatientFromIntakeResponseSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal(PatientIntakeStatus.Created),
    // Structural only: a format refinement must not turn a created patient into a failed intake.
    patient: z.object({ id: z.string(), givenName: z.string(), familyName: z.string(), dateOfBirth: z.string() }),
  }),
  z.object({ status: z.literal(PatientIntakeStatus.CarrierNotAccepted), carrier: z.string() }),
]);

export type CreatePatientFromIntakeResponse = z.infer<typeof createPatientFromIntakeResponseSchema>;

export const agentCoverageSchema = z.object({
  payer: z.string().min(1),
  plan: z.string().nullable(),
});

export type Coverage = z.infer<typeof agentCoverageSchema>;

/**
 * present/absent come from a successful appointment-history-gate classification (a match / no
 * match); unknown covers a classification failure AND a rolling-deploy backend that omits the
 * field entirely — never silently treated as "absent," since that could bypass surgical-coordinator
 * routing for a patient the check simply couldn't verify.
 */
export enum SurgeryOnChartStatus {
  Present = "present",
  Absent = "absent",
  Unknown = "unknown",
}

export const patientSchedulingContextSchema = z.object({
  /** Provider identities from this patient's chart, independent of scheduling eligibility. */
  providers: z.array(voiceProviderIdentitySchema).optional(),
  insurance: z
    .object({
      status: z.nativeEnum(PatientSchedulingInsuranceStatus),
      selfPaySchedulingAvailable: z.boolean().optional(),
      coverage: agentCoverageSchema.nullable(),
    })
    .nullish(),
  activeReferral: z
    .object({
      reason: z.string().trim().min(1).nullable(),
    })
    .nullable(),
  priorVisit: z.object({
    hasPriorVisit: z.boolean(),
    // Optional for rolling deploys where Voice updates before the backend emits this classification signal.
    hasPriorVisitAtOtherLocation: z.boolean().optional(),
    reasons: z.array(z.string().trim().min(1)),
  }),
  // Genuinely optional (no `.default()`): populated only when a group's policy opts into the
  // appointment-history surgery-on-chart gate. Groups that don't use it never need to touch this
  // field. A reader must treat a missing value as SurgeryOnChartStatus.Unknown, not "absent".
  surgeryOnChart: z.nativeEnum(SurgeryOnChartStatus).optional(),
});

export type PatientSchedulingContext = z.infer<typeof patientSchedulingContextSchema>;

export const searchPatientsResponseSchema = z.object({
  patient: agentPatientSchema,
  matchMethod: z.nativeEnum(PatientMatchMethod),
  coverage: agentCoverageSchema.nullable(),
  // Default supports rolling deploys where Voice updates before the backend starts returning this field.
  hasReferral: z.boolean().default(false),
  // Nullable/defaulted for rolling deploys. Only backend referral data may populate this field.
  referralSchedulingPriority: z.nativeEnum(SchedulingPriority).nullable().default(null),
  // Nullable/defaulted for rolling deploys and omitted unless explicitly requested.
  schedulingContext: patientSchedulingContextSchema.nullable().default(null),
  // Optional for rolling deploys where Voice updates before the backend emits this field.
  // A requesting caller must treat omission as unknown, never as present.
  contactPhoneStatus: z.nativeEnum(PatientContactPhoneStatus).optional(),
  contactPhoneNumber: northAmericanPhoneNumberSchema.nullable().optional(),
});

export type SearchPatientsResponseInput = z.input<typeof searchPatientsResponseSchema>;
export type SearchPatientsResponse = z.infer<typeof searchPatientsResponseSchema>;

const medicationRecognitionTermSchema = z.string().trim().min(1);

export const patientMedicationSchema = z.object({
  medicationId: z.string().min(1),
  medicationEntryId: z.string().min(1).nullable(),
  name: z.string().min(1),
  /** Normalized names suitable for speech-recognition hints; may fall back to the exact chart display. */
  recognitionTerms: z.array(medicationRecognitionTermSchema).default([]),
  status: z.string().min(1).nullable(),
  isDiscontinued: z.boolean(),
  isSafeToRenew: z.boolean().nullable(),
  quantity: z.string().min(1).nullable(),
  quantityUnit: z.string().min(1).nullable(),
  route: z.string().min(1).nullable(),
  unstructuredSig: z.string().min(1).nullable(),
  structuredSig: z
    .object({
      action: z.string().min(1).nullable(),
      quantityValue: z.string().min(1).nullable(),
      quantityUnit: z.string().min(1).nullable(),
      route: z.string().min(1).nullable(),
      frequencyValue: z.string().min(1).nullable(),
      frequencyUnit: z.string().min(1).nullable(),
      frequencyDescription: z.string().min(1).nullable(),
      additionalInstructions: z.string().min(1).nullable(),
    })
    .nullable(),
  prescribedBy: z.string().min(1).nullable(),
  pharmacy: z.string().min(1).nullable(),
});

export type PatientMedication = z.infer<typeof patientMedicationSchema>;

export function flattenMedicationRecognitionTerms(
  medications: readonly Pick<PatientMedication, "recognitionTerms">[]
): string[] {
  const seen = new Set<string>();
  const terms: string[] = [];

  for (const medication of medications) {
    for (const rawTerm of medication.recognitionTerms) {
      const term = rawTerm.trim();
      const normalized = term.toLocaleLowerCase();

      if (!term || seen.has(normalized)) continue;

      seen.add(normalized);
      terms.push(term);
    }
  }

  return terms;
}

export const getPatientMedicationsResponseSchema = z.object({
  medications: z.array(patientMedicationSchema),
  /** Flattened terms for speech recognition. Optional during rolling backend/Voice deploys. */
  recognitionTerms: z.array(medicationRecognitionTermSchema).nullish(),
  lastUpdated: z.string().min(1).nullable(),
  noMedicationsReported: z.boolean(),
});

export type GetPatientMedicationsResponse = z.infer<typeof getPatientMedicationsResponseSchema>;

export const createPatientCaseResponseSchema = z.object({
  success: z.literal(true),
  patientCaseId: z.string().min(1),
});

export type CreatePatientCaseResponse = z.infer<typeof createPatientCaseResponseSchema>;

export const patientNotFoundResponseSchema = z.object({
  error: z.string().min(1),
  code: z.literal("patient_not_found"),
});

export interface OpenAppointment {
  appointmentId: string;
  appointmentType: string;
  appointmentTypeId: string;
  date: string;
  startTime: string;
  duration: number;
  departmentId: string;
  providerId: string;
  patientAppointmentTypeName?: string;
}

export interface SearchOpenAppointmentsResponse {
  appointments: OpenAppointment[];
}

/**
 * Voice drops actions it does not know yet (e.g. added by a backend that deployed first) instead of
 * failing the read. Dropping an action can only narrow what the agent may do.
 */
const knownSchedulingActionsSchema = z
  .array(z.string())
  .transform((actions) =>
    actions.filter((action): action is SchedulingAction => Object.values<string>(SchedulingAction).includes(action))
  );

export const confirmedAppointmentSchema = z.object({
  ...schedulingServiceSchema.shape,
  appointmentId: z.string(),
  /** Internal Valerie appointment row id (for post-booking workflows). */
  valerieAppointmentId: z.string().optional(),
  appointmentType: z.string().optional(),
  appointmentTypeId: z.string().optional(),
  // Cancellation can return an empty datetime after an incomplete adapter response.
  dateTime: z.string(),
  duration: z.number().optional(),
  departmentId: z.string(),
  /** Internal Valerie location ID. `departmentId` remains the EHR-facing location identifier. */
  locationId: z.string().nullable().optional(),
  locationName: z.string().nullable().optional(),
  providerId: z.string().optional(),
  providerFirstName: z.string().nullable().optional(),
  providerLastName: z.string().nullable().optional(),
  providerDisplayName: z.string().nullable(),
  patientId: z.string(),
  status: z.string().optional(),
});

export type ConfirmedAppointment = z.infer<typeof confirmedAppointmentSchema>;

export const bookAppointmentResponseSchema = z.object({
  // A status Voice does not know yet must not turn a completed booking into an uncertain one.
  selfPayFollowupStatus: z.nativeEnum(SelfPayFollowupStatus).optional().catch(undefined),
  confirmedAppointment: confirmedAppointmentSchema.extend({
    locationId: z.string(),
    locationName: z.string(),
  }),
});

export type BookAppointmentResponse = z.infer<typeof bookAppointmentResponseSchema>;

/** Recoverable scheduling conflict returned when an offered slot is no longer bookable. */
export const APPOINTMENT_SLOT_NOT_AVAILABLE_ERROR = "appointment_slot_not_available";
/** The booking write may have succeeded and must not be replayed within the active call. */
export const APPOINTMENT_BOOKING_STATE_UNCERTAIN_ERROR = "appointment_booking_state_uncertain";
export const APPOINTMENT_UNIT_STATE_UNCERTAIN_ERROR = "appointment_unit_state_uncertain";

export const cancelAppointmentResponseSchema = z.object({
  cancelledAppointment: confirmedAppointmentSchema,
});

export type CancelAppointmentResponse = z.infer<typeof cancelAppointmentResponseSchema>;

export const bookedAppointmentSchema = z.object({
  ...schedulingServiceSchema.shape,
  id: z.string(),
  appointmentDatetime: z.string(),
  type: z.string(),
  reason: z.string().nullable(),
  status: z.string().nullable(),
  /** Canonical EHR appointment ID. Integration-only appointments use appointmentActionId instead. */
  ehrId: z.string().nullable(),
  /** Signed application-owned action reference. Callers must store it opaquely and keep it from the model. */
  appointmentActionId: z.string().optional(),
  practitionerId: z.string().nullable(),
  practitionerFirstName: z.string().nullable(),
  practitionerLastName: z.string().nullable(),
  practitionerDisplayName: z.string().nullable(),
  durationMinutes: z.number().nullable().optional(),
  locationId: z.string().nullable().optional(),
  locationName: z.string().nullable().optional(),
  /** The clinic's booked appointment type name. Runtime-only scope-classifier input, never shown to the model. */
  ehrAppointmentTypeName: z.string().min(1).optional(),
  // Actions the agent may take on THIS appointment. An empty list marks a read-only record,
  // including historical rescheduled appointments. Undefined = no per-appointment restriction.
  allowedActions: knownSchedulingActionsSchema.optional(),
  /**
   * Actions whose visit category was authoritatively allowed by configured appointment policy.
   * Runtime orchestration may trust this instead of reclassifying lossy type/reason text.
   */
  configuredPolicyAuthorizedActions: knownSchedulingActionsSchema.optional(),
  /** Runtime-only semantic tags from configured rules that directly matched this appointment. */
  appointmentPolicyTags: z.array(z.string()).optional(),
  /** Backend-authored atomic reschedule unit. IDs are Valerie appointment IDs, not EHR credentials. */
  rescheduleUnit: z
    .object({
      appointmentIds: z.tuple([z.string().min(1), z.string().min(1)]),
      presentation: schedulingPresentationSchema.optional(),
      maxGapMinutes: z.number(),
      sameLocation: z.boolean(),
      preserveMemberOrder: z.boolean(),
      /** Backend-authored scheduling strategy for this member of the unit. */
      targetStrategy: z.enum(["eligible_provider", "preserve_source_resource"]),
    })
    .optional(),
});

export type BookedAppointment = z.infer<typeof bookedAppointmentSchema>;

export const getBookedAppointmentsResponseSchema = z.object({
  readMetadata: bookedAppointmentsReadMetadataSchema.optional(),
  appointments: z.array(bookedAppointmentSchema),
  searchedDateRanges: z.array(z.string()),
});

export type GetBookedAppointmentsResponse = z.infer<typeof getBookedAppointmentsResponseSchema>;

export enum AppointmentSlotPreferenceTier {
  Preferred = "preferred",
  Fallback = "fallback",
}

export const appointmentSlotPreferenceTierSchema = z.nativeEnum(AppointmentSlotPreferenceTier);

export const availabilityAppointmentSlotResponseSchema = z.object({
  ...schedulingServiceSchema.shape,
  appointmentId: z.string().min(1),
  /** Opaque integrity capability. Keep it out of model-visible tool results and return it unchanged on mutation. */
  appointmentSlotActionId: versionedAppointmentSlotActionIdSchema.optional(),
  appointmentType: z.string().min(1),
  ehrAppointmentTypeId: z.string().min(1).nullable(),
  start: z.string().min(1),
  end: z.string().min(1),
  practitionerId: z.string().min(1).nullable(),
  practitionerFirstName: z.string().nullable(),
  practitionerLastName: z.string().nullable(),
  practitionerDisplayName: z.string().nullable(),
  locationId: z.string().min(1).nullable(),
  locationName: z.string().nullable(),
  preferenceTier: appointmentSlotPreferenceTierSchema.optional(),
});

export type AvailabilityAppointmentSlot = z.infer<typeof availabilityAppointmentSlotResponseSchema>;

export const availabilitySearchEmptyReasonSchema = z.enum([
  "NO_WORKING_HOURS",
  "NO_OPEN_SLOTS",
  "NO_ELIGIBLE_TARGETS",
  "NO_RECOMMENDATIONS",
  "SEARCH_INCOMPLETE",
]);

export type AvailabilitySearchEmptyReason = z.infer<typeof availabilitySearchEmptyReasonSchema>;

export interface AvailabilityResponse {
  slots: AvailabilityAppointmentSlot[];
  searchedDateRanges: string[];
  /** Non-null only when the returned candidate set is empty. */
  emptyReason?: AvailabilitySearchEmptyReason | null;
  /** Absent for sources that cannot prove exhaustive coverage (never infer it from attempted bounds). */
  completedDateRanges?: DateRange[];
}

const rescheduleOptionMoveResponseSchema = z.object({
  currentAppointmentId: z.string().min(1),
  slot: availabilityAppointmentSlotResponseSchema,
});

export enum SchedulingOptionKind {
  Single = "single",
  Slot = "slot",
  Unit = "unit",
}

// Derive wire literals from the owning enum without requiring existing callers to migrate together.
export const rescheduleAvailabilityOptionResponseSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal(`${SchedulingOptionKind.Single}`),
    id: z.string().min(1),
    presentation: schedulingPresentationSchema.optional(),
    moves: z.tuple([rescheduleOptionMoveResponseSchema]),
  }),
  z.object({
    kind: z.literal(`${SchedulingOptionKind.Unit}`),
    id: z.string().min(1),
    presentation: schedulingPresentationSchema.optional(),
    moves: z.tuple([rescheduleOptionMoveResponseSchema, rescheduleOptionMoveResponseSchema]),
  }),
]);

export const scheduleAvailabilityOptionResponseSchema = z.object({
  kind: z.literal(`${SchedulingOptionKind.Slot}`),
  id: z.string().min(1),
  slot: availabilityAppointmentSlotResponseSchema.extend({
    appointmentSlotActionId: versionedAppointmentSlotActionIdSchema,
    practitionerId: z.string().min(1),
    locationId: z.string().min(1),
  }),
});

const schedulingOptionsPageShape = {
  searchedDateRanges: z.array(z.string()),
  /** Present when policy rejected every requested date before this clinic-local date. */
  earliestPermittedDate: z.iso.date().optional(),
  // Optional for backend rollbacks; missing coverage must never be treated as exhaustive.
  searchCoverage: availabilitySearchCoverageSchema.optional(),
  // Optional keeps a newer Voice revision compatible with a backend rollback that predates
  // structured empty-search outcomes. Current backend responses always serialize this field.
  emptyReason: availabilitySearchEmptyReasonSchema.nullable().optional(),
  // Default keeps a newer Voice revision compatible with a backend rollback that predates
  // continuation support. The API always serializes the parsed output, so new responses include it.
  nextCursor: z.string().min(1).nullable().default(null),
} as const;

export const getSchedulingOptionsResponseSchema = z.discriminatedUnion("operation", [
  z.object({
    operation: z.literal(SchedulingAction.Reschedule),
    options: z.array(rescheduleAvailabilityOptionResponseSchema).max(10),
    ...schedulingOptionsPageShape,
  }),
  z.object({
    operation: z.literal(SchedulingAction.Schedule),
    options: z.array(scheduleAvailabilityOptionResponseSchema).max(10),
    ...schedulingOptionsPageShape,
  }),
]);

export type RescheduleAvailabilityOption = z.infer<typeof rescheduleAvailabilityOptionResponseSchema>;
export type ScheduleAvailabilityOption = z.infer<typeof scheduleAvailabilityOptionResponseSchema>;
export type GetSchedulingOptionsResponse = z.infer<typeof getSchedulingOptionsResponseSchema>;

export const agentLocationSchema = z.object({
  locationId: z.string(),
  locationName: z.string(),
  address: z.string().nullable().optional(),
  /** EHR department/location id used only for trusted inbound-site scoping. */
  locationEhrId: z.string().nullable().optional(),
});

export type Location = z.infer<typeof agentLocationSchema>;

export const getLocationsResponseSchema = z.object({
  locations: z.array(agentLocationSchema),
});

export type GetLocationsResponse = z.infer<typeof getLocationsResponseSchema>;

export const rescheduleAppointmentResponseSchema = z.object({
  rescheduledAppointment: bookedAppointmentSchema,
});

export type RescheduleAppointmentResponse = z.infer<typeof rescheduleAppointmentResponseSchema>;

export const ingestTranscriptResponseSchema = z.object({
  message: z.string(),
  id: z.string(),
});

export const submitVoiceCsatResponseSchema = z.object({
  id: z.string(),
});

// The reschedule-unit route parses its own output with this schema, so it keeps the strict contract.
const rescheduleUnitAppointmentSchema = z.object({
  ...schedulingServiceSchema.shape,
  id: z.string().min(1),
  appointmentDatetime: z.string().min(1),
  type: z.string().min(1),
  reason: z.string().nullable(),
  status: z.string().nullable(),
  ehrId: z.string().min(1).nullable(),
  appointmentActionId: z.string().min(1).optional(),
  practitionerId: z.string().min(1).nullable(),
  practitionerFirstName: z.string().nullable(),
  practitionerLastName: z.string().nullable(),
  practitionerDisplayName: z.string().nullable(),
  durationMinutes: z.number().int().positive().nullable().optional(),
  locationId: z.string().min(1).nullable().optional(),
  locationName: z.string().nullable().optional(),
  allowedActions: z.array(z.nativeEnum(SchedulingAction)).optional(),
});

export const rescheduleAppointmentUnitResponseSchema = z.object({
  moves: z
    .array(
      z.object({
        previousAppointmentId: z.string().min(1),
        appointment: rescheduleUnitAppointmentSchema,
      })
    )
    .length(2),
});

export type RescheduleAppointmentUnitResponse = z.infer<typeof rescheduleAppointmentUnitResponseSchema>;

export enum RecommendationType {
  ANY = "any",
  ESTABLISHED_PATIENT = "establishedPatientScheduling",
  NEW_PATIENT = "newPatientScheduling",
  RESCHEDULING = "rescheduling",
}

export enum SchedulingVisitCategory {
  NEW_PATIENT = "new_patient",
  ESTABLISHED_PATIENT = "established_patient",
  HOSPITAL_FOLLOW_UP = "hospital_follow_up",
  RESCHEDULING = "rescheduling",
}

export { CareMode };
/** @deprecated Use CareMode. Compatibility alias; existing `modality` payloads are unchanged. */
export { CareMode as SchedulingModality };

export enum SchedulingOptionStatus {
  ELIGIBLE = "eligible",
  REQUIRES_INPUT = "requires_input",
  INELIGIBLE = "ineligible",
}

export enum SchedulingRequiredInput {
  PATIENT_DECLINES_TRAVEL = "patient_declines_travel",
}

export enum SchedulingOptionIneligibilityReason {
  PROVIDER_NOT_SUPPORTED = "provider_not_supported",
  VISIT_CATEGORY_NOT_SUPPORTED = "visit_category_not_supported",
}

const schedulingOptionBaseShape = {
  appointmentType: z.nativeEnum(AppointmentType),
  modality: z.nativeEnum(CareMode),
};

export const schedulingOptionSchema = z.discriminatedUnion("status", [
  z.object({
    ...schedulingOptionBaseShape,
    status: z.literal(SchedulingOptionStatus.ELIGIBLE),
  }),
  z.object({
    ...schedulingOptionBaseShape,
    status: z.literal(SchedulingOptionStatus.REQUIRES_INPUT),
    requiredInputs: z.array(z.nativeEnum(SchedulingRequiredInput)).min(1),
  }),
  z.object({
    ...schedulingOptionBaseShape,
    status: z.literal(SchedulingOptionStatus.INELIGIBLE),
    reason: z.nativeEnum(SchedulingOptionIneligibilityReason),
  }),
]);

export const schedulingDecisionSchema = z.object({
  visitCategory: z.nativeEnum(SchedulingVisitCategory),
  options: z.array(schedulingOptionSchema).min(1),
});

export type SchedulingOption = z.infer<typeof schedulingOptionSchema>;
export type SchedulingDecision = z.infer<typeof schedulingDecisionSchema>;

export const eligibilityProviderResultSchema = z.object({
  providerName: z.string(),
  practitionerId: z.string(),
  isEligible: z.boolean(),
  /** True when this eligible provider belongs to the referral receiving-provider family. */
  isPreferredByReferral: z.literal(true).optional(),
  reasons: z.array(z.string()),
  agentInstructions: z.string().optional(),
  // The patient's soonest existing upcoming appointment, when they already have one
  // can be combined with agentInstructions to inform agent how to use this data
  upcomingAppointment: z.object({ date: z.string(), providerName: z.string() }).optional(),
  nextAppointmentDetails: z
    .object({
      approximateDate: z.string(),
      appointmentType: z.nativeEnum(AppointmentType).nullable().optional(),
    })
    .optional(),
});

export enum EligibilityUnmetRequirement {
  RECOGNIZABLE_MEDICAL_REASON = "recognizable_medical_reason",
}

export const checkEligibilityResponseSchema = z.object({
  providerEligibilityResults: z.array(eligibilityProviderResultSchema),
  /** Authoritative patient-level input still required before eligibility can be determined. */
  unmetRequirements: z.array(z.nativeEnum(EligibilityUnmetRequirement)).min(1).optional(),
  reason: z.string().nullable().optional(),
  diagnosis_list: z
    .array(z.object({ diagnosis_text: z.string().nullable() }))
    .nullable()
    .optional(),
  /** Optional for rolling compatibility while scheduling-decision-aware consumers deploy. */
  schedulingDecision: schedulingDecisionSchema.optional(),
});

export type EligibilityProviderResult = z.infer<typeof eligibilityProviderResultSchema>;
export type CheckEligibilityResponse = z.infer<typeof checkEligibilityResponseSchema>;

export interface EligibilityLocationResult {
  locationName: string | null;
  locationId: string;
  isEligible: boolean;
  reasons: string[];
  agentInstructions?: string;
}

/**
 * Output of the agentic eligibility sub-agent: a flat, isEligible-flagged determination of which
 * candidate providers/locations the patient may be scheduled with for the given scenario. Consumed
 * by the ranking sub-agent (which uses only the isEligible entries).
 */
export interface AgenticEligibilityResponse {
  recommendationType: RecommendationType;
  providerResults: EligibilityProviderResult[];
  locationResults: EligibilityLocationResult[];
}

/** A single ranked open slot. appointmentSlot is the verbatim slot returned by getProviderAvailability. */
export interface RankedAppointment {
  rank?: number | null;
  providerName?: string | null;
  locationName?: string | null;
  reasoning?: string | null;
  appointmentSlot: AvailabilityAppointmentSlot;
}

export enum RankingNoRecommendationsCategory {
  NoEligibleProvidersOrLocations = "no_eligible_providers_or_locations",
  NoOpenSlots = "no_open_slots",
  PatientChoiceRequired = "patient_choice_required",
}

/** Why the ranking sub-agent produced zero recommendations. Mutually exclusive with a non-empty list. */
export interface RankingNoRecommendationsReason {
  category: RankingNoRecommendationsCategory;
  explanation: string;
}

/**
 * Output of the agentic ranking sub-agent: the ranked open appointments for the patient. Exactly one
 * of `recommendations` (non-empty) / `noRecommendationsReason` (non-null) is populated per response.
 */
export interface AgenticRankedAppointmentsResponse {
  recommendations: RankedAppointment[];
  noRecommendationsReason: RankingNoRecommendationsReason | null;
}
